import type { CoreDepartment, CoreEmployee, UserLabel } from '@/lib/zifekCore';

interface Props {
  departments: CoreDepartment[];
  employees: CoreEmployee[];
  orgNameById: Record<number, string>;
  users: Record<number, UserLabel>;
}

export default function DepartmentsPanel({ departments, employees, orgNameById, users }: Props) {
  if (departments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-node-tree text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-600">Aucun département.</p>
      </div>
    );
  }

  const deptById = new Map(departments.map((d) => [d.id, d]));
  const empCount = (deptId: number) => employees.filter((e) => e.department_id === deptId).length;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {departments.map((d) => {
        const inactive = d.status === 'inactive';
        const parent = d.parent_department_id ? deptById.get(d.parent_department_id) : null;
        const manager = d.manager_user_id ? users[d.manager_user_id] : undefined;
        const count = empCount(d.id);
        return (
          <div key={d.id} className={`bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col ${inactive ? 'opacity-60' : ''}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-900 flex items-center justify-center flex-shrink-0">
                  <i className="ri-node-tree text-lg"></i>
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-foreground-950 truncate">{d.name}</div>
                  <div className="text-[11px] text-foreground-400 truncate">
                    {d.organization_id ? (orgNameById[d.organization_id] || `Org #${d.organization_id}`) : '—'}
                  </div>
                </div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${inactive ? 'bg-foreground-100 text-foreground-600' : 'bg-accent-100 text-accent-900'}`}>
                {inactive ? 'Désactivé' : 'Actif'}
              </span>
            </div>
            {d.description && <p className="text-xs text-foreground-500 mb-3 line-clamp-2">{d.description}</p>}
            <div className="text-xs text-foreground-500 space-y-1">
              <div className="flex items-center gap-1.5"><i className="ri-node-tree text-foreground-400"></i><span className="truncate">{parent ? `Rattaché à ${parent.name}` : 'Département principal'}</span></div>
              <div className="flex items-center gap-1.5"><i className="ri-user-star-line text-foreground-400"></i><span className="truncate">{manager?.name || 'Responsable non défini'}</span></div>
            </div>
            <div className="mt-3 pt-3 border-t border-background-200/60 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-background-100 text-foreground-700 rounded-full px-2.5 py-1">
                <i className="ri-team-line"></i> {count} employé{count > 1 ? 's' : ''}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}